"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import screenfull from "screenfull";

import type { RefObject } from "react";

const HIDE_CONTROLS_DELAY_MS = 2000;
const PROGRESS_UPDATE_INTERVAL_MS = 125;
const SEEKING_RESET_DELAY_MS = 150;
const VISIBILITY_THRESHOLD = 0.3;

export const MAX_PLAYED = 0.999_999;

type SliderValue = number | readonly number[];

const clampPlayed = (value: number) => Math.min(Math.max(value, 0), MAX_PLAYED);

interface UseVisibilityAutoPauseProps {
  containerRef: RefObject<HTMLDivElement | null>;
  fullscreen: boolean;
  src: string;
}

interface UseSmoothPlayedProgressProps {
  playerRef: RefObject<HTMLVideoElement | null>;
  src: string;
  playing: boolean;
  controlsShown: boolean;
  fullscreen: boolean;
}

export const useVideoControlsVisibility = () => {
  const [controlsShown, setControlsShown] = useState(false);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearControlsTimer = useCallback(() => {
    if (controlsTimerRef.current) {
      clearTimeout(controlsTimerRef.current);
      controlsTimerRef.current = null;
    }
  }, []);

  const showControls = useCallback(() => {
    setControlsShown(true);
    clearControlsTimer();
    controlsTimerRef.current = setTimeout(() => {
      setControlsShown(false);
    }, HIDE_CONTROLS_DELAY_MS);
  }, [clearControlsTimer]);

  const hideControls = useCallback(() => {
    clearControlsTimer();
    setControlsShown(false);
  }, [clearControlsTimer]);

  useEffect(
    () => () => {
      clearControlsTimer();
    },
    [clearControlsTimer],
  );

  return {
    controlsShown,
    showControls,
    hideControls,
  };
};

export const useVideoFullscreen = (
  containerRef: RefObject<HTMLDivElement | null>,
) => {
  const [fullscreen, setFullscreen] = useState(false);

  const syncFullscreenState = useCallback(() => {
    if (screenfull.isEnabled) {
      setFullscreen(screenfull.isFullscreen);
    }
  }, []);

  useEffect(() => {
    if (!screenfull.isEnabled) {
      return;
    }

    screenfull.on("change", syncFullscreenState);

    return () => {
      screenfull.off("change", syncFullscreenState);
    };
  }, [syncFullscreenState]);

  const enterFullscreen = useCallback(async () => {
    const element = containerRef.current;
    if (element && screenfull.isEnabled) {
      await screenfull.request(element);
    }
  }, [containerRef]);

  const exitFullscreen = useCallback(async () => {
    if (screenfull.isEnabled) {
      await screenfull.exit();
    }
  }, []);

  return {
    fullscreen,
    enterFullscreen,
    exitFullscreen,
  };
};

export const useVideoLoadingState = (src: string) => {
  const [isLoading, setIsLoading] = useState(true);

  const startLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    setIsLoading(true);
  }, [src]);

  return {
    isLoading,
    startLoading,
    stopLoading,
  };
};

export const useVisibilityAutoPause = ({
  containerRef,
  fullscreen,
  src,
}: UseVisibilityAutoPauseProps) => {
  const [isAutoPaused, setIsAutoPaused] = useState(true);
  const isIntersectingRef = useRef(false);
  const fullscreenRef = useRef(fullscreen);

  useEffect(() => {
    fullscreenRef.current = fullscreen;

    if (fullscreen) {
      setIsAutoPaused(false);
    } else if (!isIntersectingRef.current) {
      setIsAutoPaused(true);
    }
  }, [fullscreen]);

  useEffect(() => {
    isIntersectingRef.current = false;
    setIsAutoPaused(true);
  }, [src]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          return;
        }

        const isVisible = entry.intersectionRatio >= VISIBILITY_THRESHOLD;
        isIntersectingRef.current = isVisible;

        if (fullscreenRef.current) {
          setIsAutoPaused(false);
          return;
        }

        setIsAutoPaused(!isVisible);
      },
      {
        threshold: [0, VISIBILITY_THRESHOLD, 1],
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [containerRef]);

  return {
    isAutoPaused,
  };
};

export const useSmoothPlayedProgress = ({
  playerRef,
  src,
  playing,
  controlsShown,
  fullscreen,
}: UseSmoothPlayedProgressProps) => {
  const [played, setPlayed] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const lastProgressSyncRef = useRef(0);
  const seekingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekingRef = useRef(false);

  const clearSeekingTimer = useCallback(() => {
    if (seekingTimerRef.current) {
      clearTimeout(seekingTimerRef.current);
      seekingTimerRef.current = null;
    }
  }, []);

  const syncPlayedState = useCallback(
    (force = false) => {
      const player = playerRef.current;
      if (!player || seekingRef.current || !player.duration) {
        return;
      }

      const now = performance.now();
      if (
        !force &&
        now - lastProgressSyncRef.current < PROGRESS_UPDATE_INTERVAL_MS
      ) {
        return;
      }

      lastProgressSyncRef.current = now;
      setPlayed(player.currentTime / player.duration);
    },
    [playerRef],
  );

  const handleTimeUpdate = useCallback(() => {
    syncPlayedState(true);
  }, [syncPlayedState]);

  const handleSeekChange = useCallback(
    (data: SliderValue) => {
      const value = typeof data === "number" ? data : (data[0] ?? 0);
      const nextPlayed = clampPlayed(value);
      const player = playerRef.current;
      const duration = player?.duration;

      setPlayed(nextPlayed);

      if (!player || !duration) {
        return;
      }

      seekingRef.current = true;
      player.currentTime = nextPlayed * duration;
      lastProgressSyncRef.current = 0;
      clearSeekingTimer();
      seekingTimerRef.current = setTimeout(() => {
        seekingRef.current = false;
      }, SEEKING_RESET_DELAY_MS);
    },
    [clearSeekingTimer, playerRef],
  );

  const handleSeeked = useCallback(() => {
    seekingRef.current = false;
    clearSeekingTimer();
  }, [clearSeekingTimer]);

  useEffect(() => {
    setPlayed(0);
    lastProgressSyncRef.current = 0;
    seekingRef.current = false;
    clearSeekingTimer();
  }, [clearSeekingTimer, src]);

  useEffect(() => {
    if (!playing || (!controlsShown && !fullscreen)) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      return;
    }

    const updatePlayed = () => {
      syncPlayedState();
      animationFrameRef.current = requestAnimationFrame(updatePlayed);
    };

    animationFrameRef.current = requestAnimationFrame(updatePlayed);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [controlsShown, fullscreen, playing, syncPlayedState]);

  useEffect(
    () => () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      clearSeekingTimer();
    },
    [clearSeekingTimer],
  );

  return {
    played,
    handleSeekChange,
    handleSeeked,
    handleTimeUpdate,
  };
};
